/**
 * ActivityFeed — Monday-style per-item event timeline (Phase 1, 2026-05-16).
 *
 * Usage:
 *   <ActivityFeed itemType="investment" itemId="INV-GUNB-32374" />
 *   <ActivityFeed itemType="tender" itemId={tenderId} compact />
 *   <ActivityFeed global limit={50} />  // dashboard widget
 *
 * Features:
 *   - Auto-fetch + paginate (cursor based)
 *   - Manual note input with @mentions
 *   - Live WebSocket updates (jeśli ws_manager broadcastuje activity channel)
 *   - Event grouping by day
 *   - Icon + color from EVENT_TYPES dictionary
 */

import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import {
  Send, MessageSquare, RefreshCw, Loader2, Bot,
} from 'lucide-react';
import {
  getItemActivity, addActivityNote, getRecentActivity, listActivityEventTypes,
} from '../../lib/api';
import { toast } from 'sonner';


// Colorset: shadcn-friendly Tailwind classes for badges
const COLOR_BG = {
  primary:  'bg-primary/15 text-primary',
  emerald:  'bg-emerald-500/15 text-emerald-300',
  amber:    'bg-amber-500/15 text-amber-300',
  red:      'bg-red-500/15 text-red-300',
  blue:     'bg-blue-500/15 text-blue-300',
  cyan:     'bg-cyan-500/15 text-cyan-300',
  indigo:   'bg-indigo-500/15 text-indigo-300',
  purple:   'bg-purple-500/15 text-purple-300',
  zinc:     'bg-zinc-500/15 text-zinc-300',
};


function fmtTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

function fmtDateGroup(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const today = new Date();
    const yest = new Date();
    yest.setDate(today.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return 'Dzisiaj';
    if (d.toDateString() === yest.toDateString()) return 'Wczoraj';
    return d.toLocaleDateString('pl-PL', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch { return iso.slice(0, 10); }
}

function describeEvent(evt, meta) {
  const p = evt.payload || {};
  const sub = evt.event_subtype;
  switch (evt.event_type) {
    case 'stage_changed':
      return `${p.from || '?'} → ${p.to || '?'}${p.note ? ` · ${p.note}` : ''}`;
    case 'decision_chain_updated':
      return `${sub || 'rola'}: ${p.name || p.company || '—'}`;
    case 'decision_maker_verified':
      return `${sub || 'rola'}: ${p.name || ''} (✓ zweryfikowany)`;
    case 'adam_pattern_hit':
      return `pattern z ${p.based_on_count || '?'} projektów (${p.pattern_type || ''})`;
    case 'auto_research_run':
      return `źródła: ${(p.sources_used || []).join(', ') || 'brak'}`;
    case 'spec_in_added':
      return `${p.product_name || p.product_id || 'produkt'}${p.specced_by ? ` (przez ${p.specced_by})` : ''}`;
    case 'spec_in_removed':
      return `usunięto: ${p.product_id || ''}`;
    case 'outreach_logged':
      return `${p.role || ''} · ${p.channel || ''} · ${p.contact_name || ''}`;
    case 'outreach_response_received':
      return `${p.role || ''} odpowiedział`;
    case 'email_sent':
      return `do: ${p.to || ''} · ${(p.subject || '').slice(0, 80)}`;
    case 'email_received':
      return `od: ${p.from || ''} · ${(p.subject || '').slice(0, 80)}`;
    case 'note_added':
      return p.text || '';
    case 'swz_changed':
      return p.summary || 'wykryto zmianę';
    case 'tender_submitted':
      return p.summary || 'oferta złożona';
    case 'agent_run':
      return `${p.action || ''}`;
    default:
      return Object.entries(p)
        .filter(([k]) => !['raw', 'doc'].includes(k))
        .slice(0, 3)
        .map(([k, v]) => `${k}=${typeof v === 'object' ? '…' : String(v).slice(0, 40)}`)
        .join(' · ');
  }
}


function ActivityItem({ evt, meta }) {
  const m = meta || {};
  const color = COLOR_BG[m.color] || COLOR_BG.zinc;
  const isAgent = evt.actor?.agent_name;
  const actorLabel = isAgent
    ? evt.actor.agent_name
    : (evt.actor?.user_name || 'system');
  return (
    <div className="flex items-start gap-2 group">
      <div className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm ${color}`}>
        {m.icon || '•'}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 text-xs">
          <span className="font-medium text-foreground/90 truncate">
            {m.label_pl || evt.event_type}
          </span>
          {isAgent && <Bot className="w-3 h-3 text-purple-300" />}
          <span className="text-muted-foreground truncate">{actorLabel}</span>
          <span className="text-muted-foreground/70 ml-auto whitespace-nowrap text-[10px]">
            {fmtTime(evt.created_at)}
          </span>
        </div>
        {describeEvent(evt, m) && (
          <div className="text-xs text-muted-foreground mt-0.5 break-words">
            {describeEvent(evt, m)}
          </div>
        )}
      </div>
    </div>
  );
}


export default function ActivityFeed({
  itemType,
  itemId,
  global = false,
  limit = 50,
  compact = false,
  enableNotes = true,
  enableWebSocket = true,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [eventMeta, setEventMeta] = useState({});
  const [noteText, setNoteText] = useState('');
  const [posting, setPosting] = useState(false);
  const wsRef = useRef(null);

  // Load event_types dictionary once
  useEffect(() => {
    listActivityEventTypes()
      .then(d => {
        const map = {};
        (d.event_types || []).forEach(t => { map[t.key] = t; });
        setEventMeta(map);
      })
      .catch(() => {});
  }, []);

  const load = useCallback(async (cursor = null) => {
    try {
      if (cursor) setLoadingMore(true);
      else setLoading(true);

      const params = { limit };
      if (cursor) params.before_ts = cursor;

      const res = global
        ? await getRecentActivity(params)
        : await getItemActivity(itemType, itemId, params);

      if (cursor) {
        setItems(prev => [...prev, ...(res.items || [])]);
      } else {
        setItems(res.items || []);
      }
      setNextCursor(res.next_cursor || null);
    } catch (err) {
      if (!cursor) toast.error('Nie udało się załadować historii');
      // eslint-disable-next-line no-console
      console.warn('Activity load failed:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [global, itemType, itemId, limit]);

  useEffect(() => {
    if (global || (itemType && itemId)) {
      load();
    }
  }, [global, itemType, itemId, load]);

  // Live WebSocket subscription
  useEffect(() => {
    if (!enableWebSocket) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = window.location.host;
      const ws = new WebSocket(`${proto}//${host}/api/ws?token=${encodeURIComponent(token)}`);
      wsRef.current = ws;

      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.channel !== 'activity' || !msg.payload) return;
          const p = msg.payload;
          // Filter: only events matching current item (or global mode)
          if (!global) {
            if (p.item_type !== itemType) return;
            if (String(p.item_id) !== String(itemId)) return;
          }
          if (p.activity) {
            setItems(prev => {
              // Dedup by id
              if (prev.some(x => x.id === p.activity.id)) return prev;
              return [p.activity, ...prev];
            });
          }
        } catch { /* ignore */ }
      };
      ws.onerror = () => { /* silent */ };
      return () => { try { ws.close(); } catch { /* */ } };
    } catch {
      return undefined;
    }
  }, [enableWebSocket, global, itemType, itemId]);

  const handlePostNote = async () => {
    const text = noteText.trim();
    if (!text || global || !itemType || !itemId) return;
    try {
      setPosting(true);
      await addActivityNote(itemType, itemId, text);
      setNoteText('');
      toast.success('Notatka dodana');
      // Reload first page (WS może już dodać, fallback dla braku WS)
      load();
    } catch (err) {
      toast.error('Nie udało się dodać notatki');
    } finally {
      setPosting(false);
    }
  };

  // Group by day
  const grouped = useMemo(() => {
    const groups = [];
    let last = null;
    for (const evt of items) {
      const key = (evt.created_at || '').slice(0, 10);
      if (key !== last) {
        groups.push({ key, label: fmtDateGroup(evt.created_at), items: [] });
        last = key;
      }
      groups[groups.length - 1].items.push(evt);
    }
    return groups;
  }, [items]);

  if (loading) {
    return (
      <div className="space-y-2 py-3">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="flex items-start gap-2">
            <Skeleton className="w-7 h-7 rounded-full" />
            <div className="flex-1 space-y-1">
              <Skeleton className="h-3 w-1/2" />
              <Skeleton className="h-3 w-3/4" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={compact ? 'space-y-3' : 'space-y-4'}>
      {/* Note composer */}
      {enableNotes && !global && itemType && itemId && (
        <Card>
          <CardContent className="p-3 space-y-2">
            <Textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Dodaj notatkę… (Ctrl+Enter aby wysłać, @user aby wzmiankować)"
              rows={2}
              className="resize-none text-sm"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault();
                  handlePostNote();
                }
              }}
            />
            <div className="flex items-center justify-between">
              <div className="text-[10px] text-muted-foreground flex items-center gap-1">
                <MessageSquare className="w-3 h-3" />
                Notatki są widoczne dla całego zespołu
              </div>
              <Button
                size="sm"
                onClick={handlePostNote}
                disabled={!noteText.trim() || posting}
                className="h-7 text-xs"
              >
                {posting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                Wyślij
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Timeline */}
      {grouped.length === 0 ? (
        <div className="text-xs text-muted-foreground text-center py-6">
          Brak zdarzeń. Każda akcja (zmiana etapu, weryfikacja kontaktu, email…) pojawi się tutaj.
        </div>
      ) : (
        <div className="space-y-4">
          {grouped.map((group) => (
            <div key={group.key}>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2 pl-1">
                {group.label}
                <Badge variant="outline" className="ml-1 text-[10px] h-4 px-1">
                  {group.items.length}
                </Badge>
              </div>
              <div className="space-y-2">
                {group.items.map((evt) => (
                  <ActivityItem
                    key={evt.id}
                    evt={evt}
                    meta={eventMeta[evt.event_type]}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {nextCursor && (
        <div className="text-center pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => load(nextCursor)}
            disabled={loadingMore}
            className="text-xs"
          >
            {loadingMore
              ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              : <RefreshCw className="w-3 h-3 mr-1" />}
            Załaduj starsze
          </Button>
        </div>
      )}
    </div>
  );
}
